import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Badge,
  LoadingSkeleton,
} from '@nexora/design-system';
import { apiClient } from '../../shared/api/client';

/* ── Types (contrato real: ApplicationResponseDto) ── */

interface IntegrationApp {
  id: string;
  name: string;
  description: string | null;
  scopes: string[];
  active: boolean;
  createdAt: string;
}

interface BackendApplication {
  id: string;
  name: string;
  description?: string | null;
  scopes?: string[] | null;
  isActive?: boolean | null;
  createdAt: string;
}

/* ── Hooks ── */

function useIntegrationApps() {
  return useQuery({
    queryKey: ['integration', 'apps'],
    // Contrato real: GET /api/v1/integration/applications (paginado)
    queryFn: async (): Promise<IntegrationApp[]> => {
      const res = await apiClient.get<
        BackendApplication[] | { data: BackendApplication[] }
      >('/integration/applications');
      const items = Array.isArray(res) ? res : (res.data ?? []);
      return items.map((app) => ({
        id: app.id,
        name: app.name,
        description: app.description ?? null,
        scopes: app.scopes ?? [],
        active: app.isActive ?? false,
        createdAt: app.createdAt,
      }));
    },
  });
}

/* ── Component ── */

export function IntegrationsListPage(): JSX.Element {
  const { data, isLoading, error } = useIntegrationApps();

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <div className="integration-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Integrações</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Aplicações integradas ao tenant.</p>
        </div>
      </div>

      <Card>
        {isLoading && (
          <div className="loading-container">
            <LoadingSkeleton count={4} height="32px" />
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">Erro ao carregar integrações: {(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && (!data || data.length === 0) && (
          <div className="empty-container">
            <p className="empty-message">Nenhuma integração configurada.</p>
            <p className="empty-hint">As integrações são gerenciadas através do módulo de Webhooks. Acesse <strong>Integrações &gt; Webhooks</strong> para configurar.</p>
          </div>
        )}

        {!isLoading && !error && data && data.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Escopos</th>
                  <th>Status</th>
                  <th>Criado em</th>
                </tr>
              </thead>
              <tbody>
                {data.map((app) => (
                  <tr key={app.id}>
                    <td className="cell-name">{app.name}</td>
                    <td>{app.description ?? '—'}</td>
                    <td>
                      <div className="scope-tags">
                        {app.scopes.slice(0, 3).map((s) => (
                          <Badge key={s} variant="neutral">{s}</Badge>
                        ))}
                        {app.scopes.length > 3 && <span className="more-scopes">+{app.scopes.length - 3}</span>}
                      </div>
                    </td>
                    <td>
                      <Badge variant={app.active ? 'success' : 'neutral'}>
                        {app.active ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </td>
                    <td>{formatDate(app.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <style>{`
        .integration-page { padding: 1.5rem; }
        .loading-container { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .error-container { padding: 2rem; text-align: center; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .empty-container { padding: 2rem; text-align: center; }
        .empty-message { color: #74777f; font-size: 0.875rem; margin-bottom: 0.5rem; }
        .empty-hint { color: #74777f; font-size: 0.8125rem; max-width: 400px; margin: 0 auto; line-height: 1.5; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .data-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .data-table tbody tr:hover td { background: #eceef0; }
        .cell-name { font-weight: 600; }
        .scope-tags { display: flex; gap: 0.25rem; flex-wrap: wrap; align-items: center; }
        .more-scopes { font-size: 0.75rem; color: #74777f; }
      `}</style>
    </div>
  );
}
